// C7a — context-aware Map legend. Explains the EXISTING station-marker visual
// semantics; it changes no colour, threshold, size or classification.
//
// Source of truth: every swatch colour is produced by CALLING the same
// functions the markers use (`markerColor` / `markerColorForSelection` /
// `drColor` / `markerDiameter`) — never by copying HSL / literals / thresholds.
// The legend switches between the overview (no config selected) and per-config
// modes so the grey swatch never carries two meanings on screen at once.

import type { ReactNode } from 'react';

import type { MapStation } from '../../api/types';
import { drColor } from '../analytics/HeatmapStationConfig';
import { markerColor, markerColorForSelection, markerDiameter } from './StationMarker';

import './MapLegend.css';

// Synthetic stations fed to the REAL colour functions so the swatches can never
// drift from the markers. `__legend__` is a sentinel config that exists only
// here to drive the per-config «tried, no result» (black) branch.
const SLEEPING: MapStation = {
  code: '', latitude: 0, longitude: 0, country_code: null, description: null, city: null,
  total_jobs: 0, done_with_residual: 0, median_dr_mm: null, best_dr_mm: null,
  best_job_id: null, best_config: null, last_activity_at: null, active_now: false, per_config: [],
};
const TRIED_NO_RESULT: MapStation = {
  ...SLEEPING, total_jobs: 5, done_with_residual: 0,
  per_config: [{ config: '__legend__', job_count: 1, had_attempt: true, median_dr_mm: null }],
};
const GREY = markerColor(SLEEPING); // { fill: 'var(--ink-3)', opacity: 0.4 }
const BLACK = markerColorForSelection(TRIED_NO_RESULT, ['__legend__']); // { fill: '#000', … }
// Accuracy gradient sampled from `drColor` across the |dR| range (left = small
// |dR| = better, right = large |dR| = worse). Qualitative — no invented stops.
const GRADIENT = `linear-gradient(to right, ${drColor(0.5)}, ${drColor(5)}, ${drColor(20)}, ${drColor(60)})`;
// Size swatch dots use the REAL diameter mapping (fewest vs most done-residual).
const SIZE_SMALL = markerDiameter(1);
const SIZE_LARGE = markerDiameter(10);

function Dot({ fill, opacity }: { fill: string; opacity: number }) {
  return (
    <span
      className="map-legend-swatch map-legend-swatch-dot"
      style={{ background: fill, opacity }}
      aria-hidden="true"
    />
  );
}
function Item({ testid, swatch, text }: { testid: string; swatch: ReactNode; text: string }) {
  return (
    <span className="map-legend-item" data-testid={testid}>
      {swatch}
      <span className="map-legend-text">{text}</span>
    </span>
  );
}

export function MapLegend({ perConfig }: { perConfig: boolean }) {
  return (
    <div
      className="map-legend"
      data-testid="map-legend"
      data-mode={perConfig ? 'per-config' : 'default'}
      role="group"
      aria-label="Легенда карты"
    >
      <span className="map-legend-title">
        {perConfig ? 'Легенда · выбранная конфигурация' : 'Легенда'}
      </span>

      <Item
        testid="map-legend-accuracy"
        swatch={
          <span
            className="map-legend-swatch map-legend-swatch-gradient"
            style={{ background: GRADIENT }}
            aria-hidden="true"
          />
        }
        text={
          perConfig
            ? 'Есть измеренная точность для выбранной конфигурации — меньше |dR| точнее, больше хуже'
            : 'Точность |dR| — меньше точнее, больше хуже'
        }
      />

      {perConfig ? (
        <>
          <Item
            testid="map-legend-black"
            swatch={<Dot fill={BLACK.fill} opacity={BLACK.opacity} />}
            text="Пробовали — результата нет (конфигурация применялась, чистого residual/median нет)"
          />
          <Item
            testid="map-legend-grey"
            swatch={<Dot fill={GREY.fill} opacity={GREY.opacity} />}
            text="Конфигурация не относится к станции (у станции могут быть результаты по другим конфигурациям)"
          />
        </>
      ) : (
        <Item
          testid="map-legend-grey"
          swatch={<Dot fill={GREY.fill} opacity={GREY.opacity} />}
          text="Нет измеренной точности (задач нет либо нет чистого результата для median |dR|)"
        />
      )}

      <Item
        testid="map-legend-active"
        swatch={
          <span className="map-legend-swatch map-legend-swatch-pulse" aria-hidden="true">
            <span className="map-legend-pulse-ring" />
            <span className="map-legend-pulse-dot" />
          </span>
        }
        text="Идёт обработка"
      />

      <Item
        testid="map-legend-size"
        swatch={
          <span className="map-legend-swatch map-legend-swatch-size" aria-hidden="true">
            <i style={{ width: SIZE_SMALL, height: SIZE_SMALL }} />
            <i style={{ width: SIZE_LARGE, height: SIZE_LARGE }} />
          </span>
        }
        text="Размер маркера — количество задач с рассчитанной точностью"
      />
    </div>
  );
}
